/**
 * Shared styled components for tag-style cell editors.
 * Used by EmailTagCellEditor, StringTagCellEditor, and similar editors.
 */
import {
    colorAttentionBlue10,
    colorAttentionRed50,
    colorFunctionalGray10,
    colorFunctionalGray30,
    colorLayoutBorder,
    colorMiscBlue20,
    colorMiscBlue70,
    colorMiscGray90,
    fontSizeM,
    fontSizeS,
    spacingS,
    spacingSm,
    spacingXs,
    spacingXxs,
} from "@rhumbix/rmbx_design_system_web"
import styled from "styled-components"

export const TagEditorContainer = styled.div`
    width: 100%;
    min-width: 300px;
    padding: ${spacingXs};
    background: white;
    border: 1px solid ${colorLayoutBorder};
    border-radius: ${spacingXs};
    position: relative;
`

export const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacingXs};
    align-items: center;
    min-height: 32px;
    padding: ${spacingXs};
`

export const Tag = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${colorAttentionBlue10};
    border-radius: ${spacingXs};
    height: 30px;
    max-width: 100%;
    padding: ${spacingXxs} ${spacingXs} ${spacingXxs} ${spacingS};
    font-size: ${fontSizeM};
    color: ${colorMiscBlue70};

    &:hover {
        background-color: ${colorMiscBlue20};
    }
`

export const TagText = styled.span`
    flex: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: ${spacingS};
    text-align: left;
`

export const RemoveButton = styled.button`
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    min-height: 30px;
    max-width: 30px;
    border: none;
    background: transparent;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    border-radius: ${spacingXxs};
`

export const TagInput = styled.input`
    flex: 1;
    min-width: 150px;
    border: none;
    outline: none;
    font-size: ${fontSizeM};
    padding: ${spacingXs};
    background: transparent;

    &::placeholder {
        color: ${colorFunctionalGray30};
    }
`

export const ErrorMessage = styled.div`
    color: ${colorAttentionRed50};
    font-size: ${fontSizeS};
    margin-top: ${spacingXs};
    padding: 0 ${spacingXs};
`

/** Dropdown container for autocomplete suggestions, positioned below the input. */
export const SuggestionsContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid ${colorLayoutBorder};
    border-top: none;
    border-radius: 0 0 ${spacingXs} ${spacingXs};
    max-height: 150px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 ${spacingXs} 6px rgba(0, 0, 0, 0.1);
`

/** Individual suggestion item with hover and selected highlighting. */
export const SuggestionItem = styled.div<{ isSelected: boolean }>`
    padding: ${spacingS} ${spacingSm};
    cursor: pointer;
    font-size: ${fontSizeS};
    color: ${colorMiscGray90};
    transition: background-color 0.2s;
    background-color: ${props => (props.isSelected ? colorFunctionalGray10 : "transparent")};

    &:hover {
        background-color: ${colorFunctionalGray10};
    }

    &:first-child {
        border-top: 1px solid ${colorLayoutBorder};
    }
`
